// ═══════════════════════════════════════════════════════════════════════════
// Cliente HTTP de Facturapi
// Extraído VERBATIM de index.html v0.9.16.
// ═══════════════════════════════════════════════════════════════════════════
//
// ⚠ La llave vive en localStorage y viaja al navegador. En la Etapa 4 pasa
//   al servidor: una llave de timbrado no puede estar en el equipo de un
//   vendedor.

import { descargarArchivo, compartirOdescargar } from "../pdf/guardar.js";
import { getWorkerProxyUrl } from "../datos/nube.js";
import { SAT_PRODUCT_KEY_WINE, SAT_UNIT_KEY_BOTTLE } from "./catalogos-sat.js";
import { mapFormaPago, mapMetodoPago, mapUsoCfdi } from "./cfdi.js";

export const FACTURAPI_BASE_URL = "https://www.facturapi.io/v2";

export const DEFAULT_PROXIES = [
  "https://corsproxy.io/?url=",
  "https://api.allorigins.win/raw?url=",
];

export function getDefaultProxiesWithWorker() {
  const worker = getWorkerProxyUrl();
  return worker ? [worker, ...DEFAULT_PROXIES] : [...DEFAULT_PROXIES];
}

export function getFacturapiConfig() {
  try {
    const cfg = JSON.parse(localStorage.getItem("vt-facturapi") || "{}");
    const defaultProxies = getDefaultProxiesWithWorker();
    return {
      apiKey: cfg.apiKey || "",
      isLive: cfg.isLive || false,
      enabled: !!cfg.apiKey,
      proxyUrl: cfg.proxyUrl || "",
      proxies: cfg.proxies ? prependWorkerProxy(cfg.proxies) : defaultProxies,
      lastWorkingProxy: cfg.lastWorkingProxy || "",
      userKey: cfg.userKey || "",
    };
  } catch { return { apiKey: "", isLive: false, enabled: false, proxyUrl: "", proxies: getDefaultProxiesWithWorker(), lastWorkingProxy: "", userKey: "" }; }
}

export function prependWorkerProxy(proxies) {
  const worker = getWorkerProxyUrl();
  if (!worker) return proxies;
  const filtered = proxies.filter(p => !p.includes("/fapi"));
  return [worker, ...filtered];
}

export function saveFacturapiConfig(cfg) {
  localStorage.setItem("vt-facturapi", JSON.stringify(cfg));
}

export function buildUrlWithProxy(endpoint, proxyUrl) {
  const target = FACTURAPI_BASE_URL + endpoint;
  if (!proxyUrl) return target;
  const proxy = proxyUrl.trim().replace(/\/+$/, "");
  // Worker proxy: /fapi/v2/endpoint (direct path, no encoding)
  if (proxy.endsWith("/fapi")) return proxy + "/v2" + endpoint;
  // URL-encoding proxies: ?url=<encoded>
  if (proxy.includes("?url=") || proxy.includes("?target=")) return proxy + encodeURIComponent(target);
  // Path-based proxies: proxy/target
  return proxy + "/" + target;
}

export async function facturapiRequest(endpoint, method = "GET", body = null) {
  const cfg = getFacturapiConfig();
  if (!cfg.apiKey) throw new Error("No hay API key de Facturapi configurada");
  const opts = {
    method,
    headers: { "Authorization": "Bearer " + cfg.apiKey, "Content-Type": "application/json" },
  };
  if (body) opts.body = JSON.stringify(body);

  // Build ordered list: last working proxy first, then the rest
  const proxies = [...cfg.proxies];
  if (cfg.lastWorkingProxy && !proxies.includes(cfg.lastWorkingProxy)) proxies.unshift(cfg.lastWorkingProxy);
  if (cfg.lastWorkingProxy) {
    const idx = proxies.indexOf(cfg.lastWorkingProxy);
    if (idx > 0) { proxies.splice(idx, 1); proxies.unshift(cfg.lastWorkingProxy); }
  }

  const errors = [];
  for (const proxy of proxies) {
    try {
      const url = buildUrlWithProxy(endpoint, proxy);
      console.log(`[Facturapi] Trying proxy: ${proxy || "(direct)"} → ${url.substring(0, 80)}...`);
      const res = await fetch(url, { ...opts });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Error " + res.status }));
        throw new Error(err.message || JSON.stringify(err));
      }
      // Success! Remember this proxy
      if (proxy !== cfg.lastWorkingProxy) {
        saveFacturapiConfig({ ...cfg, lastWorkingProxy: proxy });
      }
      return res;
    } catch (err) {
      const isNetwork = err.message === "Failed to fetch" || err.name === "TypeError";
      errors.push({ proxy: proxy || "(directo)", error: err.message, isNetwork });
      // If it's a non-network error (e.g. 400 bad request), don't try other proxies
      if (!isNetwork) throw err;
    }
  }

  // All proxies failed
  const tried = errors.map(e => `• ${e.proxy}: ${e.error}`).join("\n");
  throw new Error(
    "No se pudo conectar a Facturapi con ningún proxy.\n\n" +
    "Proxies intentados:\n" + tried + "\n\n" +
    "Ve a Config → Facturapi para agregar o editar proxies."
  );
}

export async function facturapiRawFetch(endpoint) {
  const cfg = getFacturapiConfig();
  if (!cfg.apiKey) throw new Error("No hay API key");
  const headers = { "Authorization": "Bearer " + cfg.apiKey };

  const proxies = [...cfg.proxies];
  if (cfg.lastWorkingProxy) {
    const idx = proxies.indexOf(cfg.lastWorkingProxy);
    if (idx > 0) { proxies.splice(idx, 1); proxies.unshift(cfg.lastWorkingProxy); }
  }

  for (const proxy of proxies) {
    try {
      const url = buildUrlWithProxy(endpoint, proxy);
      const res = await fetch(url, { headers });
      if (res.ok) {
        if (proxy !== cfg.lastWorkingProxy) saveFacturapiConfig({ ...cfg, lastWorkingProxy: proxy });
        return res;
      }
    } catch { /* try next */ }
  }
  throw new Error("No se pudo descargar — todos los proxies fallaron");
}

export async function timbrarFactura(invoiceData, products) {
  const cl = invoiceData.client;
  // El régimen de ESTA remisión manda sobre el del cliente, igual que ya
  // pasa con la forma de pago, el método y el uso de CFDI. Un cliente con
  // varios regímenes se factura con el que se eligió, no con el primero.
  const taxSystem = invoiceData.regimenFiscal || cl.taxSystem || "612";
  const fiscalZip = cl.domicilioFiscalZip || cl.cp || "00000";
  const discountPct = invoiceData.discount || 0;
  const ivaRate = invoiceData.ivaRate / 100;

  // Build items array with per-product IEPS (exclude zero-price cortesía items)
  const items = invoiceData.lines.filter(li => {
    const price = li.unitPrice || (li.amount / li.qty);
    return price > 0;
  }).map(li => {
    const prod = products.find(p => p.id === li.productId);
    const price = li.unitPrice || li.amount / li.qty;
    const lineIepsRate = li.iepsRate || prod?.iepsRate || 26.5;

    // Round discount to 2 decimal places per CFDI spec
    const lineSubtotal = Math.round(price * li.qty * 100) / 100;
    const lineDiscount = discountPct > 0
      ? Math.round(lineSubtotal * (discountPct / 100) * 100) / 100
      : undefined;

    return {
      quantity: li.qty,
      discount: lineDiscount,
      product: {
        description: prod?.name || li.productName || "Vino",
        product_key: SAT_PRODUCT_KEY_WINE,
        unit_key: SAT_UNIT_KEY_BOTTLE,
        unit_name: "Botella",
        price: Math.round(price * 100) / 100,
        tax_included: false,
        taxes: [
          { type: "IEPS", rate: lineIepsRate / 100, factor: "Tasa" },
          { type: "IVA", rate: ivaRate, factor: "Tasa" },
        ],
      },
    };
  });

  const body = {
    customer: {
      legal_name: cl.legalName || cl.name,
      tax_id: cl.rfc,
      tax_system: taxSystem,
      email: cl.email || undefined,
      address: { zip: fiscalZip },
    },
    items,
    payment_form: invoiceData.formaPago || mapFormaPago(cl.paymentMethod),
    payment_method: invoiceData.metodoPago || mapMetodoPago(cl.paymentTiming),
    use: invoiceData.usoCfdi || mapUsoCfdi(cl.cfdiUse),
    folio_number: invoiceData.folio,
    series: "VT",
  };

  console.log("[Timbrar] Request body:", JSON.stringify(body, null, 2));
  console.log("[Timbrar] Remisión totals:", {
    subtotalBeforeDiscount: invoiceData.subtotalBeforeDiscount,
    discountAmount: invoiceData.discountAmount,
    subtotal: invoiceData.subtotal,
    iepsAmount: invoiceData.iepsAmount,
    ivaAmount: invoiceData.ivaAmount,
    ivaRate: invoiceData.ivaRate,
    total: invoiceData.total,
  });

  const res = await facturapiRequest("/invoices", "POST", body);
  const result = await res.json();

  console.log("[Timbrar] Facturapi response:", JSON.stringify(result, null, 2));

  // Extract CFDI totals from response for comparison
  if (result.total !== undefined) {
    const diff = Math.abs((result.total || 0) - (invoiceData.total || 0));
    console.log("[Timbrar] CFDI total:", result.total, "vs Remisión total:", invoiceData.total, "Diff:", diff.toFixed(2));
    if (diff > 1) {
      console.warn("[Timbrar] ⚠️ Diferencia significativa entre CFDI y remisión:", diff.toFixed(2));
    }
  }

  return result;
}

/** Trae el PDF o el XML del CFDI sin hacer nada con él: `{ blob, nombre }`. */
export async function obtenerFacturapi(invoiceId, formato) {
  const res = await facturapiRawFetch("/invoices/" + invoiceId + "/" + formato);
  const blob = await res.blob();
  return { blob, nombre: "CFDI_" + invoiceId + "." + formato };
}

export async function downloadFacturapiPdf(invoiceId) {
  const { blob, nombre } = await obtenerFacturapi(invoiceId, "pdf");
  descargarArchivo(blob, nombre);
}

export async function downloadFacturapiXml(invoiceId) {
  // Un XML no se mira: se comparte o se descarga, sin pestaña nueva.
  const { blob, nombre } = await obtenerFacturapi(invoiceId, "xml");
  await compartirOdescargar(blob, nombre);
}

export async function cancelarFactura(invoiceId, motive, substitutionId = null) {
  const body = { motive };
  if (motive === "01" && substitutionId) body.substitution = substitutionId;
  console.log("🚫 Cancelando CFDI:", invoiceId, "Motivo:", motive, body);
  const res = await facturapiRequest("/invoices/" + invoiceId, "DELETE", body);
  const data = await res.json();
  console.log("🚫 Respuesta cancelación:", data);
  return data;
}

export async function downloadAcuseCancelacion(invoiceId, format = "pdf") {
  const res = await facturapiRawFetch("/invoices/" + invoiceId + "/cancellation_receipt/" + format);
  const blob = await res.blob();
  descargarArchivo(blob, "AcuseCancelacion_" + invoiceId + "." + format);
}

export async function facturapiUserRequest(endpoint, method = "GET", body = null) {
  const cfg = getFacturapiConfig();
  const userKey = cfg.userKey;
  if (!userKey) throw new Error("No hay User Key de Facturapi configurada");

  const opts = {
    method,
    headers: { "Authorization": "Bearer " + userKey, "Content-Type": "application/json" },
  };
  if (body) opts.body = JSON.stringify(body);

  const proxies = [...(cfg.proxies || [])];
  if (cfg.lastWorkingProxy && !proxies.includes(cfg.lastWorkingProxy)) proxies.unshift(cfg.lastWorkingProxy);
  if (cfg.lastWorkingProxy) {
    const idx = proxies.indexOf(cfg.lastWorkingProxy);
    if (idx > 0) { proxies.splice(idx, 1); proxies.unshift(cfg.lastWorkingProxy); }
  }

  for (const proxy of proxies) {
    try {
      const url = buildUrlWithProxy(endpoint, proxy);
      const res = await fetch(url, opts);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Error " + res.status }));
        throw new Error(err.message || JSON.stringify(err));
      }
      return res;
    } catch (err) {
      if (err.message !== "Failed to fetch" && err.name !== "TypeError") throw err;
    }
  }
  throw new Error("No se pudo conectar a Facturapi");
}
